import React from "react";

import httpr from "lib/httpRequest";
import { BASE_URL, isLocal } from "lib/config";

import ReactLoading from "react-loading";
import Tooltip from "@material-ui/core/Tooltip";

import AlertTable from "components/Table/AlertTable.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";

class ThresholdAlerts extends React.Component {
  _isMounted = false;
  constructor(props) {
    super(props);

    this.state = {
      alerts: [],
      isLoading: true,
      expiredLocations: [],
      expiringLocations: [],
      hasFetched: false,
    };
  }

  // Helper functions to check license status (matching MachineErrors pattern)
  getLocationLicenseStatus = (locationName, locationLicenses) => {
    if (!locationLicenses || locationLicenses.length === 0) {
      return null;
    }
    const licenseData = locationLicenses.find(
      loc => {
        if (!loc.locationName) return false;
        const normalizedApiName = locationName.replace(/_/g, " ").trim().toLowerCase();
        const normalizedLicenseName = loc.locationName.trim().toLowerCase();
        if (normalizedApiName === normalizedLicenseName) return true;
        const normalizedApiNameSpaces = normalizedApiName.replace(/\s+/g, " ");
        const normalizedLicenseNameSpaces = normalizedLicenseName.replace(/\s+/g, " ");
        return normalizedApiNameSpaces === normalizedLicenseNameSpaces;
      }
    );
    return licenseData ? licenseData.license : null;
  };

  isLocationExpired = (locationName, locationLicenses) => {
    const license = this.getLocationLicenseStatus(locationName, locationLicenses);
    if (!license) return false;
    return license.isValid === 0;
  };

  isLocationExpiringSoon = (locationName, locationLicenses) => {
    const license = this.getLocationLicenseStatus(locationName, locationLicenses);
    if (!license || license.isValid === 0) return false;
    const daysUntilExpiration = license.daysUntilExpiration;
    return daysUntilExpiration !== null && daysUntilExpiration <= 30 && daysUntilExpiration > 0;
  };

  getDaysUntilExpiration = (locationName, locationLicenses) => {
    const license = this.getLocationLicenseStatus(locationName, locationLicenses);
    if (!license) return null;
    return license.daysUntilExpiration;
  };

  componentDidMount() {
    this._isMounted = true;
    if (this.props.locationLicenses && 
        this.props.locationLicenses.length > 0) {
      this.setState({ isLoading: true });
      this.getThresholdAlerts(this.props.locationLicenses);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps, prevState) {
    const settingsChanged = 
      prevProps.memberSettings.minThreshold !== this.props.memberSettings.minThreshold ||
      prevProps.memberSettings.maxThreshold !== this.props.memberSettings.maxThreshold;
    
    const licenseDataChanged = prevProps.locationLicenses !== this.props.locationLicenses;
    const licenseDataAvailable = this.props.locationLicenses && 
      this.props.locationLicenses.length > 0 && 
      (!prevProps.locationLicenses || prevProps.locationLicenses.length === 0);

    if (settingsChanged && this.props.locationLicenses && this.props.locationLicenses.length > 0 && this._isMounted === true) {
      this.setState({ 
        isLoading: true, 
        alerts: [],
        expiredLocations: [],
        expiringLocations: []
      });
      this.getThresholdAlerts(this.props.locationLicenses);
    } else if (licenseDataAvailable && !this.state.hasFetched && this._isMounted === true) {
      this.setState({ isLoading: true });
      this.getThresholdAlerts(this.props.locationLicenses);
    } else if (licenseDataChanged && this.props.locationLicenses && this.props.locationLicenses.length > 0) {
      this.setState({ 
        isLoading: true,
        hasFetched: false,
        alerts: [],
        expiredLocations: [],
        expiringLocations: []
      });
      this.getThresholdAlerts(this.props.locationLicenses);
    }
  }

  getThresholdAlerts = (locationLicenses = []) => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);
    fetch
      .post(`${BASE_URL}/api/alerts/threshold`, {
        minThreshold: this.props.memberSettings.minThreshold,
        maxThreshold: this.props.memberSettings.maxThreshold,
      })
      .then((response) => {
        if (this._isMounted === true) {
          const alerts = response.data || [];
          
          const expiredLocations = [];
          const expiringLocations = [];
          const validAlerts = [];
          
          alerts.forEach((alertObj) => {
            Object.keys(alertObj).forEach((locationName) => {
              const isExpired = this.isLocationExpired(locationName, locationLicenses);
              const isExpiringSoon = this.isLocationExpiringSoon(locationName, locationLicenses);
              
              if (isExpired) {
                expiredLocations.push({
                  locationName: locationName,
                });
              } else {
                validAlerts.push(alertObj);
                
                if (isExpiringSoon) {
                  expiringLocations.push({
                    locationName: locationName,
                    daysUntilExpiration: this.getDaysUntilExpiration(locationName, locationLicenses)
                  });
                }
              }
            });
          });
          
          this.setState({
            alerts: validAlerts,
            expiredLocations: expiredLocations,
            expiringLocations: expiringLocations,
            isLoading: false,
            hasFetched: true,
          });
        }
      })
      .catch((e) => {
        console.log(e);
        if (this._isMounted === true) {
          this.setState({ isLoading: false });
        }
      });
  }

  render() {
    const { classes, locationLicenses = [] } = this.props;

    // Render expired location cards
    const expiredLocationCards = this.state.expiredLocations.map((expiredLoc) => {
      const locationName = expiredLoc.locationName;
      return (
        <GridItem key={`${locationName}ThresholdExpiredGrid`} xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="danger">
              <h4 className={classes.cardTitleWhite}>
                🔒 {locationName.replace(/_/g, " ")} - License Expired
              </h4>
            </CardHeader>
            <CardBody>
              <div style={{ 
                padding: '20px', 
                textAlign: 'center',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                margin: '20px'
              }}>
                <h3 style={{ color: '#d32f2f', margin: '0 0 10px 0' }}>
                  🔒 License Expired
                </h3>
                <p style={{ color: '#666', margin: '0' }}>
                  This location's license has expired. {isLocal === "true" || isLocal === true 
                    ? "Please click Renew License and follow the instructions to send a renewal token to our support team."
                    : "Please contact our support team for renewal."}
                </p>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      );
    });

    // Render alert cards for valid/expiring locations
    const alertCards = this.state.alerts.map((alertsByLocation) => {
      return Object.keys(alertsByLocation).map((locationName) => {
        const locData = alertsByLocation[locationName];
        const isExpiringSoon = this.isLocationExpiringSoon(locationName, locationLicenses);
        const daysUntilExpiration = this.getDaysUntilExpiration(locationName, locationLicenses);
        
        let titleText = locationName.replace(/_/g, " ");
        if (isExpiringSoon) {
          titleText = `⚠️ ${titleText} - Renewal due in ${daysUntilExpiration} days`;
        }

        return (
          <GridItem
            key={`${locationName}ThresholdGrid`}
            xs={12}
            sm={12}
            md={12}
          >
            <Card>
              {isExpiringSoon ? (
                <Tooltip
                  title={`License renewal due soon. ${isLocal === "true" || isLocal === true 
                    ? "Please click Renew License and follow the instructions to send a renewal token to our support team."
                    : "Please contact our support team for renewal."}`}
                  placement="top"
                  arrow
                >
                  <div>
                    <CardHeader color="warning">
                      <h4 className={classes.cardTitleWhite}>
                        {titleText}
                      </h4>
                    </CardHeader>
                  </div>
                </Tooltip>
              ) : (
                <CardHeader color="success">
                  <h4 className={classes.cardTitleWhite}>
                    {titleText}
                  </h4>
                </CardHeader>
              )}
              <CardBody>
                <AlertTable
                  key={`${locationName}Threshold`}
                  tableHeaderColor="primary"
                  tableData={locData}
                  tableTitle=""
                  tableType="Threshold"
                />
              </CardBody>
            </Card>
          </GridItem>
        );
      });
    });

    const allCards = [...expiredLocationCards, ...alertCards];

    return (
      <div>
        {this.state.isLoading === true ? (
          <CardBody>
            <ReactLoading
              type="spin"
              color="#000000"
              style={{
                width: "20%",
                margin: "auto",
              }}
            />
          </CardBody>
        ) : (
          <GridContainer>{allCards}</GridContainer>
        )}
      </div>
    );
  }
}

export default ThresholdAlerts;



// WEBPACK FOOTER //
// ./src/components/Alerts/ThresholdAlerts.jsx